import {
  FETCH_ORDERS_ERROR_MESSAGE,
  FETCHING_SLIDES_ERROR_MESSAGE,
} from 'constants/errorMessages';
import { useLazyGetSlidesByOrderForManageDataQuery } from 'store/apis/slidesApi';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyGetIFSlidesByOrderForManageDataQuery } from 'store/apis/ifSlideApi';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import {
  useArchiveOrdersMutation,
  useContributeOrdersMutation,
  useExportOrdersOnHardDiskMutation,
  useGetExportedOnHardDiskOrdersQuery,
  useGetOrdersForManageDataQuery,
} from 'store/apis/orderApi';
import { useQueryError } from 'utilities/hooks/useQueryError/useQueryError';
import { filterOrdersByTeam } from 'components/UserProfile/utilities';
import { LOADING_STATE } from 'components/TeamOverview/constants';
import { ORDER_TYPE_FILTERS } from 'components/TeamOverview/utilities';
import {
  HE_SLIDES_COUNT_ATTRIBUTE,
  IF_SLIDES_COUNT_ATTRIBUTE,
  IF_SLIDES_DATA_USED_ATTRIBUTE,
  IHC_SLIDES_COUNT_ATTRIBUTE,
  SLIDES_COUNT_ATTRIBUTE,
  SLIDES_DATA_USED_ATTRIBUTE,
} from 'utilities/constants';

export const useSlidesForOrder = () => {
  const { showError } = useSnackbar();

  const [selectedRowsSlides, setSelectedRowsSlides] = useState(new Map());

  const [getSlidesByOrderForManageData] =
    useLazyGetSlidesByOrderForManageDataQuery();
  const [getIFSlidesByOrderForManageData] =
    useLazyGetIFSlidesByOrderForManageDataQuery();

  const fetchSlidesForOrder = async (order) => {
    try {
      setSelectedRowsSlides((prevMap) => {
        prevMap.set(order, LOADING_STATE);

        return new Map(prevMap);
      });

      const slides = await getSlidesByOrderForManageData({
        orderUUID: order.uuid,
      }).unwrap();
      const ifSlides = await getIFSlidesByOrderForManageData({
        orderUUID: order.uuid,
      }).unwrap();
      // TODO: ask Jeff and remove if not needed
      // const publicOnlySlides = slides
      //   ? slides.filter((slide) => slide.is_public)
      //   : [];
      // const publicOnlyIfSlides = ifSlides
      //   ? ifSlides.filter((ifSlide) => ifSlide.is_public)
      //   : [];

      setSelectedRowsSlides((prevMap) => {
        prevMap.set(order, {
          slides: slides || [],
          ifSlides: ifSlides || [],
        });

        return new Map(prevMap);
      });
    } catch (e) {
      showError(FETCHING_SLIDES_ERROR_MESSAGE);
    }
  };

  return {
    fetchSlidesForOrder,
    selectedRowsSlides,
  };
};

export const useSteps = (steps, defaultStepIdx = 0) => {
  const [stepIDX, setStepIDX] = useState(null);

  const handleNextStep = (step) => {
    setStepIDX(step || ((prevIDX) => prevIDX + 1));
  };

  const handlePrevStep = () => {
    setStepIDX((prevIDX) => (prevIDX - 1 < 0 ? 0 : prevIDX - 1));
  };

  const resetStep = () => {
    setStepIDX(defaultStepIdx);
  };

  const handleLastStep = () => {
    setStepIDX(steps.length - 1);
  };

  const currentStep = steps[stepIDX];

  return {
    currentStep,
    currentStepIDX: stepIDX,
    handleNextStep,
    handlePrevStep,
    handleLastStep,
    resetStep,
  };
};

export const useOrdersForDataManagement = (
  team,
  selectedOrdersType,
  isDemoAccount,
) => {
  const { showError } = useSnackbar();

  const {
    data: rawOrders,
    isLoading,
    error,
  } = useGetOrdersForManageDataQuery([
    HE_SLIDES_COUNT_ATTRIBUTE,
    IHC_SLIDES_COUNT_ATTRIBUTE,
    IF_SLIDES_COUNT_ATTRIBUTE,
    SLIDES_COUNT_ATTRIBUTE,
    IF_SLIDES_DATA_USED_ATTRIBUTE,
    SLIDES_DATA_USED_ATTRIBUTE,
  ]);

  const { data: exportedOrders, error: HDDOrdersError } =
    useGetExportedOnHardDiskOrdersQuery(undefined, { skip: isDemoAccount });
  // Backend returns 404 for users with no permissions
  if (HDDOrdersError) {
    showError(HDDOrdersError);
  }

  useQueryError(error, FETCH_ORDERS_ERROR_MESSAGE);

  const mergedWithExported = rawOrders?.map((order) => {
    const exportedOrdersHistory = exportedOrders?.filter((exportedOrder) =>
      exportedOrder.orders.some((o) => o.uuid === order.uuid),
    );

    return {
      ...order,
      exportedOrdersHistory: exportedOrdersHistory || [],
      exported: !!exportedOrdersHistory?.length,
    };
  });

  const ordersFilteredByTeam = filterOrdersByTeam(mergedWithExported, [team]);

  const ordersFilteredByType =
    ORDER_TYPE_FILTERS[selectedOrdersType](ordersFilteredByTeam);

  return {
    isLoading,
    orders: ordersFilteredByType,
    ordersForHistory: ordersFilteredByTeam,
  };
};

export const useOrdersActions = () => {
  const [contributeOrders] = useContributeOrdersMutation();
  const [exportOrdersOnHardDisk] = useExportOrdersOnHardDiskMutation();
  const [archiveOrders] = useArchiveOrdersMutation();

  const archive = async (selectedOrders) => {
    const orderUUIDs = selectedOrders.map(
      (order) => `order_uuids=${order.uuid}`,
    );
    const queryParams = orderUUIDs.join('&');

    return archiveOrders({
      queryParams,
      orders: selectedOrders,
    }).unwrap();
  };

  const contribute = (selectedOrders, team, userName) => {
    const ordersList = selectedOrders.map((order) => ({
      uuid: order.uuid,
      pathology_map_consent: 'True',
    }));

    const postParams = {
      orders: ordersList,
      client_name: userName,
      team_uuid: team.uuid,
    };

    return contributeOrders({
      postParams,
    }).unwrap();
  };

  const exportOrders = (selectedOrders, teamUUID, shipping_address_uuid) => {
    const postParams = {
      order_uuids: selectedOrders.map((order) => order.uuid),
      team_uuid: teamUUID,
      shipping_address_uuid,
    };

    return exportOrdersOnHardDisk({
      postParams,
    }).unwrap();
  };

  return {
    contribute,
    archive,
    exportOrders,
  };
};

export const useFixInternalSidebarDuringScroll = () => {
  const [isSticky, setIsSticky] = useState(false);
  const containerRef = useRef(null);
  const sidebarRef = useRef(null);
  const sidebarPlaceholderRef = useRef(null);

  const main = document.getElementsByTagName('main')[0];
  const header = document.getElementsByTagName('header')[0];
  const headerHeight = header?.getBoundingClientRect()?.height || 0;

  const { paddingRight, paddingBottom } = main
    ? window.getComputedStyle(main)
    : {};

  const setWidth = useCallback(() => {
    sidebarRef.current.style.width = `${
      sidebarPlaceholderRef.current.getBoundingClientRect().width
    }px`;
  }, []);

  useEffect(() => {
    const sidebar = sidebarRef.current;

    if (sidebar) {
      Object.assign(sidebar.style, {
        position: isSticky ? 'fixed' : 'relative',
        top: isSticky ? `${headerHeight}px` : 'auto',
        right: isSticky ? paddingRight : 'auto',
        height: isSticky
          ? `calc(100dvh - ${headerHeight}px - ${paddingBottom})`
          : '100%',
      });
      setWidth();
    }
  }, [headerHeight, isSticky, paddingBottom, paddingRight, setWidth]);

  useEffect(() => {
    if (sidebarPlaceholderRef.current && sidebarRef.current) {
      window.addEventListener('resize', setWidth);

      return () => {
        window.removeEventListener('resize', setWidth);
      };
    }
  }, [setWidth]);

  useEffect(() => {
    const container = containerRef.current;

    const handleFixInternalSidebar = () => {
      const containerTop = container.getBoundingClientRect().top;

      setIsSticky(headerHeight >= containerTop);
    };

    if (container && sidebarRef.current) {
      document.addEventListener('scroll', handleFixInternalSidebar);

      return () => {
        document.removeEventListener('scroll', handleFixInternalSidebar);
      };
    }
  }, [headerHeight]);

  return { containerRef, sidebarRef, sidebarPlaceholderRef };
};
